import { Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  DeleteReferralResponse,
  ReferralCountRequest,
  ReferralCountResponse,
  ReferralListRequest,
  ReferralExportRequest,
  PaginatedReferralResponse,
  Referral,
  SaveReferralRequest,
} from "../../Models/Referral";
import { ClientConfig } from "../../Models/Client-Nav";

export interface ReferralExportButtonState {
  visible: boolean;
  disabled: boolean;
  isExporting: boolean;
  tooltip: string;
}

@Injectable({
  providedIn: "root",
})
export class ReferralService {
  private apiUrl = environment.apiUrl;
  private readonly _referralRefreshDate = new BehaviorSubject<string>('');
  readonly referralRefreshDate$ = this._referralRefreshDate.asObservable();

  // Export button state, shared with the app header so the button can be
  // rendered next to "Last refreshed" while the referral component keeps
  private readonly _exportButtonState = signal<ReferralExportButtonState>({
    visible: false,
    disabled: true,
    isExporting: false,
    tooltip: '',
  });
  readonly exportButtonState = this._exportButtonState.asReadonly();

  private readonly _exportTrigger = new Subject<void>();
  readonly exportTrigger$ = this._exportTrigger.asObservable();

  constructor(private http: HttpClient) {}

  setExportButtonState(state: ReferralExportButtonState): void {
    this._exportButtonState.set(state);
  }

  triggerExport(): void {
    this._exportTrigger.next();
  }

    getReferralsPaginated(payload: ReferralListRequest): Observable<PaginatedReferralResponse> {
    return this.http.post<PaginatedReferralResponse>(`${this.apiUrl}/referrals/list`, payload);
  }

  exportReferrals(payload: ReferralExportRequest): Observable<{ downloadURL: string }> {
    return this.http.post<{ downloadURL: string }>(`${this.apiUrl}/referrals/export`, payload);
  }

  getReferralsCount(payload: ReferralCountRequest): Observable<ReferralCountResponse> {
    return this.http.post<ReferralCountResponse>(`${this.apiUrl}/referrals/count`, payload);
  }

  getReferralById(id: string): Observable<Referral> {
    return this.http.get<Referral>(`${this.apiUrl}/referrals/${id}`);
  }

  createReferral(payload: SaveReferralRequest, userId: string): Observable<Referral> {
    return this.http.post<Referral>(`${this.apiUrl}/referrals/create`, { ...payload, userId });
  }

  updateReferral(id: string, payload: SaveReferralRequest, userId: string): Observable<Referral> {
    return this.http.put<Referral>(`${this.apiUrl}/referrals/update/${id}`, { ...payload, userId });
  }

  deleteReferral(id: string, userId: string): Observable<DeleteReferralResponse> {
    return this.http.delete<DeleteReferralResponse>(`${this.apiUrl}/referrals/delete/${id}`, {
      params: { userId },
    });
  }

  getConfigList(): Observable<ClientConfig[]> {
    return this.http.get<ClientConfig[]>(`${this.apiUrl}/referrals/get-config-list`);
  }

  setReferralRefreshDate(value: string): void {
    this._referralRefreshDate.next(value);
  }

  public addConfigValue(configKey: string, value: string): Observable<void> {
  return this.http.post<void>(`${this.apiUrl}/config/${configKey}/values`, { value });
}
}
